import {
  ConditionAction,
  ConditionName,
  Option,
  SYNC_SELECTED_CONDITION,
  SYNC_SORT_ORDER,
  RangeConditionName,
  PeriodConditionName,
  CheckConditionName,
  RadioConditionName,
  OptionSetType,
  SET_TAGS,
  ADD_TAGS_TO_KEYWORDS,
  OPEN_STATUS_CONDITION,
  SELECT_FILTER_STATUS_CONDITION,
  StatusConditionAction,
  SET_INIT_STATUS_CONDITION,
  SET_INIT_CONTACT_PERSON_CONDITION,
  SELECT_FILTER_STATUS_UPDATED,
  RESET_OFFSET,
  SET_OPTIONS,
} from '../actions'
import {
  CHANGE_KEYWORDS,
  CHANGE_PAGE,
  ADD_CONDITION,
  REMOVE_CONDITION,
  TOGGLE_OPTION,
  SET_RANGE,
  SET_PERIOD,
  TOGGLE_SORT,
  RangeParams,
  PeriodParams,
  RADIO_CONDITION_NAMES,
  SET_PAGE,
} from '../actions'
import { SortableField, SortingOrder } from '../constants/orders'
import { Conditions as ConditionsConstant } from '../constants/conditions'
import _ from 'lodash'
import {
  SELECT_FILTER_FIRST_INTERVIEW_DATE,
  SELECT_FILTER_MY_LABEL,
  SELECT_FILTER_SOURCING_ROUTE,
  SET_INIT_FIRST_INTERVIEW_DATE,
  SET_INIT_MY_LABEL_CONDITION,
  SET_INIT_SOURCING_ROUTE_CONDITION,
  SET_INIT_STATUS_UPDATED_AT,
} from '../actions/statusCondition'
import { MyLabelId } from './sourcingAnnotationCandidates'

export type SortState = {
  sort?: SortableField | null
  order?: SortingOrder | null
}

export type ConditionState = SortState & {
  keywords: string
  tags: string
  limit: number
  offset: number
  page: number
  selected: ConditionsConstant
}

export const statusConditionItems = {
  status: 'status' as const,
  contactPeople: 'contactPeople' as const,
  statusUpdated: 'statusUpdated' as const,
  firstInterviewDate: 'firstInterviewDate' as const,
  sourcingRoute: 'sourcingRoute' as const,
}
export type StatusConditionItem = keyof typeof statusConditionItems

export type StatusCondition = { id: string }

export type ContactPeopleCondition = { id: number }

export type SourcingRouteId = { id: number }

export type StatusUpdatedCondition = {
  from: string | null
  to: string | null
}

export type FirstInterviewDateCondition = {
  from: string | null
  to: string | null
}

export type StatusConditions = StatusCondition | ContactPeopleCondition

export type StatusConditionState = {
  openingItem?: StatusConditionItem
  filterItems: {
    itemName: StatusConditionItem
    conditions: StatusConditions[]
  }[]
  statusUpdated: StatusUpdatedCondition
  firstInterviewDate: FirstInterviewDateCondition
  sourcingRouteIds: SourcingRouteId[]
  myLabelIds: MyLabelId[]
}

const init: ConditionState & StatusConditionState = {
  keywords: '',
  tags: '',
  limit: 50,
  offset: 0,
  page: 1,
  sort: null,
  order: null,
  selected: {
    type: [],
    latestInitialSeries: [],
    cbLatestRoundType: [],
    totalFunding: { from: null, to: null },
    valuation: { from: null, to: null },
    lastFundraised: { from: null, to: null },
    country: [],
  },
  openingItem: undefined,
  filterItems: [],
  statusUpdated: {
    from: null,
    to: null,
  },
  firstInterviewDate: {
    from: null,
    to: null,
  },
  sourcingRouteIds: [],
  myLabelIds: [],
}

export function add(
  selected: ConditionsConstant,
  name: ConditionName
): ConditionsConstant {
  if (!Object.keys(selected).includes(name.toString())) {
    selected = { ...selected, [name]: [] }
  }
  return selected
}

export function remove(
  selected: ConditionsConstant,
  name: ConditionName
): ConditionsConstant {
  if (!Object.keys(selected).includes(name.toString())) return selected
  return Object.keys(selected)
    .filter((n) => n !== name)
    .reduce(
      (p, n) => ({
        ...p,
        [n]: selected[
          <
            | RangeConditionName
            | PeriodConditionName
            | CheckConditionName
            | RadioConditionName
          >n
        ],
      }),
      {}
    )
}

export function setMultiple(
  selected: ConditionsConstant,
  name: ConditionName,
  values: Option[],
  setType: OptionSetType
) {
  const current = selected[name]
  const next = values.reduce((list, value) => {
    if (setType === 'add') {
      // @ts-ignore
      return [...list, value]
    } else {
      // @ts-ignore
      return list.filter((v) => v !== value)
    }
  }, current)
  return {
    ...selected,
    [name]: _.uniq(<any>next),
  }
}

export function toggle(
  selected: ConditionsConstant,
  name: ConditionName,
  value: Option
): ConditionsConstant {
  if (typeof selected[name] === 'undefined') {
    selected = add(selected, name)
  }

  let list = selected[name]
  if (Array.isArray(list) && list.includes(value)) {
    list = list.filter((v) => v !== value)
  } else {
    list = [...list, value]
  }

  return { ...selected, [name]: list }
}

export function select(
  selected: ConditionsConstant,
  name: ConditionName,
  value: Option
): ConditionsConstant {
  return { ...selected, [name]: [value] }
}

export function set(
  selected: ConditionsConstant,
  name: ConditionName,
  updated: RangeParams | PeriodParams
) {
  return { ...selected, [name]: updated }
}

export function nextOrder(order?: SortingOrder | null): SortingOrder | null {
  switch (order) {
    case 'DESC':
      return 'ASC'
    case 'ASC':
      return null
  }
  return 'DESC'
}

export function sort(
  { sort, order }: SortState,
  name: SortableField
): SortState {
  if (sort === name) {
    const next = nextOrder(order)
    return next === null ? { sort: null, order: null } : { sort, order: next }
  } else {
    return { sort: name, order: 'DESC' }
  }
}

const convertTagKeywords = (tagsStr: string) =>
  tagsStr
    .split(',')
    .map((t) => '#' + t)
    .join(' ')

export default function condition(
  state: ConditionState & StatusConditionState = init,
  action: ConditionAction | StatusConditionAction
) {
  switch (action.type) {
    case CHANGE_KEYWORDS:
      return { ...state, keywords: action.payload }
    case SET_TAGS:
      return { ...state, tags: action.payload }
    case SET_PAGE:
      return { ...state, page: action.payload }
    case RESET_OFFSET:
      return { ...state, offset: 0, page: 1 }
    case ADD_TAGS_TO_KEYWORDS:
      return {
        ...state,
        keywords: state.keywords
          ? `${state.keywords} ${convertTagKeywords(action.payload)}`
          : convertTagKeywords(action.payload),
      }
    case CHANGE_PAGE:
      return {
        ...state,
        offset: action.payload.offset,
        page: action.payload.page,
      }
    case ADD_CONDITION:
      return { ...state, selected: add(state.selected, action.payload) }
    case REMOVE_CONDITION:
      return { ...state, selected: remove(state.selected, action.payload) }
    case TOGGLE_OPTION:
      const op = RADIO_CONDITION_NAMES.includes(action.payload.name)
        ? select
        : toggle
      const selected = op(
        state.selected,
        action.payload.name,
        action.payload.value
      )
      return { ...state, selected }
    case SET_RANGE:
      return {
        ...state,
        selected: set(
          state.selected,
          action.payload.name,
          action.payload.range
        ),
      }
    case SET_OPTIONS:
      return {
        ...state,
        offset: 0,
        selected: setMultiple(
          state.selected,
          action.payload.name,
          action.payload.values,
          action.payload.setType
        ),
      }
    case SET_PERIOD:
      return {
        ...state,
        selected: set(
          state.selected,
          action.payload.name,
          action.payload.period
        ),
      }
    case TOGGLE_SORT:
      return {
        ...state,
        ...sort({ sort: state.sort, order: state.order }, action.payload),
      }
    case SYNC_SORT_ORDER:
      return { ...state, ...action.payload }
    case SYNC_SELECTED_CONDITION:
      return {
        ...state,
        selected: {
          ...state.selected,
          ...action.payload,
        },
      }

    case OPEN_STATUS_CONDITION:
      return {
        ...state,
        openingItem: action.payload,
      }
    case SELECT_FILTER_STATUS_CONDITION:
      const onlyStatus = state.filterItems.filter(
        (i) => i.itemName === action.payload.itemName
      )
      const exceptStatus = state.filterItems.filter(
        (i) => i.itemName !== action.payload.itemName
      )

      return {
        ...state,
        offset: 0,
        filterItems: exceptStatus.concat(
          onlyStatus.length === 0
            ? {
                itemName: action.payload.itemName,
                conditions: [action.payload.conditions],
              }
            : onlyStatus
                .map((i) => ({
                  ...i,
                  conditions: i.conditions.some(
                    (c) =>
                      JSON.stringify(c) ===
                      JSON.stringify(action.payload.conditions)
                  )
                    ? i.conditions.filter(
                        (c) =>
                          JSON.stringify(c) !==
                          JSON.stringify(action.payload.conditions)
                      )
                    : i.conditions.concat(action.payload.conditions),
                }))
                .filter((s) => s.conditions.length > 0)
        ),
      }
    case SELECT_FILTER_STATUS_UPDATED:
      return {
        ...state,
        statusUpdated: action.payload,
      }
    case SELECT_FILTER_FIRST_INTERVIEW_DATE:
      return {
        ...state,
        firstInterviewDate: action.payload,
      }
    case SELECT_FILTER_SOURCING_ROUTE:
      const isAlreadySelected = state.sourcingRouteIds.find(
        (sr) => sr.id === action.payload.id
      )
      const sourcingRouteIds = isAlreadySelected
        ? state.sourcingRouteIds.filter((sr) => sr.id !== action.payload.id)
        : state.sourcingRouteIds.concat({ id: action.payload.id })
      return {
        ...state,
        sourcingRouteIds: sourcingRouteIds,
      }
    case SELECT_FILTER_MY_LABEL:
      const myLabelIds = state.myLabelIds.find((it) => it === action.payload)
        ? state.myLabelIds.filter((it) => it !== action.payload)
        : [...state.myLabelIds, action.payload]
      return {
        ...state,
        myLabelIds: myLabelIds,
      }
    case SET_INIT_STATUS_CONDITION:
      return {
        ...state,
        filterItems: state.filterItems.concat({
          itemName: statusConditionItems.status,
          conditions: action.payload,
        }),
      }
    case SET_INIT_STATUS_UPDATED_AT:
      return {
        ...state,
        statusUpdated: action.payload,
      }
    case SET_INIT_FIRST_INTERVIEW_DATE:
      return {
        ...state,
        firstInterviewDate: action.payload,
      }
    case SET_INIT_CONTACT_PERSON_CONDITION:
      return {
        ...state,
        filterItems: state.filterItems.concat({
          itemName: statusConditionItems.contactPeople,
          conditions: action.payload,
        }),
      }
    case SET_INIT_SOURCING_ROUTE_CONDITION:
      return {
        ...state,
        sourcingRouteIds: action.payload,
      }
    case SET_INIT_MY_LABEL_CONDITION:
      return {
        ...state,
        myLabelIds: action.payload,
      }
  }
  return state
}

// TODO: このへん型の参照が循環してしまっている
type RangeConditions = { [key in RangeConditionName]?: RangeParams }
type PeriodConditions = { [key in PeriodConditionName]?: PeriodParams }
type CheckConditions = { [key in CheckConditionName]?: Option[] }
type RadioConditions = { [key in RadioConditionName]?: Option[] }

export type Conditions = RangeConditions &
  CheckConditions &
  PeriodConditions &
  RadioConditions &
  StatusConditions
